import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { money } from "@/lib/format";
import { toast } from "sonner";
import { ShieldCheck, Eye, Car, Truck, ClipboardText, Storefront, Buildings } from "@phosphor-icons/react";

const CAT_EMOJI = { bike: "🏍️", car: "🚗", truck: "🚚", van: "🚐" };

function StatusPill({ status }) {
  const tone = status === "approved" ? "bg-emerald-500/15 text-emerald-600"
    : status === "rejected" ? "bg-destructive/15 text-destructive" : "bg-amber-500/15 text-amber-600";
  return <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${tone}`}>{status}</span>;
}

function Drivers() {
  const qc = useQueryClient();
  const [notes, setNotes] = useState({});
  const [insp, setInsp] = useState({});
  const { data } = useQuery({ queryKey: ["insp-drivers"], queryFn: async () => (await api.get("/rides/admin/drivers")).data.drivers });
  const drivers = (data || []).slice().sort((a, b) => (a.approval_status === "pending" ? -1 : 1));

  const viewDoc = async (id, type) => {
    try { const res = await api.get(`/rides/driver/${id}/document/${type}`, { responseType: "blob" }); window.open(URL.createObjectURL(res.data), "_blank"); }
    catch { toast.error("Could not open document"); }
  };
  const review = async (id, approve) => {
    try { await api.post(`/rides/admin/drivers/${id}/review`, { approve, inspection_passed: !!insp[id], notes: notes[id] || "" }); toast.success(approve ? "Driver approved" : "Driver rejected"); qc.invalidateQueries({ queryKey: ["insp-drivers"] }); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-3" data-testid="insp-drivers">
      {drivers.length === 0 && <p className="rounded-2xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">No driver applications.</p>}
      {drivers.map((d) => (
        <div key={d.user_id} data-testid={`insp-driver-${d.user_id}`} className="rounded-2xl border border-border bg-card p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="text-sm font-semibold">{d.name} <span className="text-xs text-muted-foreground">· {d.email}</span></p>
              <p className="text-xs text-muted-foreground">{CAT_EMOJI[d.vehicle_type]} {d.vehicle_color} {d.vehicle_make} {d.vehicle_model} · {d.vehicle_plate || "no plate"} · Lic {d.license_number || "—"}{d.community ? ` · ${d.community}` : ""}</p>
            </div>
            <StatusPill status={d.approval_status} />
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {["license", "state_id", "registration", "insurance", "vehicle_photo", "selfie"].map((t) => (
              <button key={t} data-testid={`insp-doc-${d.user_id}-${t}`} disabled={!d.documents?.includes(t)} onClick={() => viewDoc(d.user_id, t)}
                className="flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs font-semibold disabled:opacity-30"><Eye size={13} /> {t}</button>
            ))}
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-xs"><input type="checkbox" data-testid={`insp-pass-${d.user_id}`} checked={!!insp[d.user_id]} onChange={(e) => setInsp((x) => ({ ...x, [d.user_id]: e.target.checked }))} className="h-4 w-4" /> Inspection passed</label>
            <input data-testid={`insp-notes-${d.user_id}`} value={notes[d.user_id] || ""} onChange={(e) => setNotes((x) => ({ ...x, [d.user_id]: e.target.value }))} placeholder="Notes" className="min-w-[160px] flex-1 rounded-xl border border-border bg-background px-3 py-2 text-xs outline-none focus:border-primary" />
            <button data-testid={`insp-approve-${d.user_id}`} disabled={!["license","state_id","registration","insurance","vehicle_photo","selfie"].every((t) => d.documents?.includes(t))} onClick={() => review(d.user_id, true)} className="rounded-full bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground disabled:opacity-40">Approve</button>
            <button data-testid={`insp-reject-${d.user_id}`} onClick={() => review(d.user_id, false)} className="rounded-full border border-border px-4 py-2 text-xs font-semibold hover:text-destructive">Reject</button>
          </div>
        </div>
      ))}
    </div>
  );
}

function Fleets() {
  const qc = useQueryClient();
  const [notes, setNotes] = useState({});
  const { data } = useQuery({ queryKey: ["insp-fleet"], queryFn: async () => (await api.get("/fleet/inspector/vehicles")).data.vehicles });
  const vehicles = (data || []).slice().sort((a, b) => (a.approval_status === "pending" ? -1 : 1));
  const review = async (id, approve) => {
    try { await api.post(`/fleet/inspector/vehicles/${id}/review`, { approve, notes: notes[id] || "" }); toast.success(approve ? "Vehicle approved" : "Vehicle rejected"); qc.invalidateQueries({ queryKey: ["insp-fleet"] }); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="space-y-3" data-testid="insp-fleets">
      {vehicles.length === 0 && <p className="rounded-2xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">No fleet vehicles to inspect.</p>}
      {vehicles.map((v) => (
        <div key={v.id} data-testid={`insp-fleet-${v.id}`} className="rounded-2xl border border-border bg-card p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="text-sm font-semibold">{CAT_EMOJI[v.type] || "🚗"} {v.name} <span className="text-xs text-muted-foreground">· {v.owner_email}</span></p>
              <p className="text-xs text-muted-foreground">{[v.plate || "no plate", [v.make, v.model, v.year].filter(Boolean).join(" "), `${v.odometer} mi`, v.community].filter(Boolean).join(" · ")}</p>
            </div>
            <StatusPill status={v.approval_status} />
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <input data-testid={`insp-fleet-notes-${v.id}`} value={notes[v.id] || ""} onChange={(e) => setNotes((x) => ({ ...x, [v.id]: e.target.value }))} placeholder="Inspection notes" className="min-w-[160px] flex-1 rounded-xl border border-border bg-background px-3 py-2 text-xs outline-none focus:border-primary" />
            <button data-testid={`insp-fleet-approve-${v.id}`} onClick={() => review(v.id, true)} className="rounded-full bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground">Approve</button>
            <button data-testid={`insp-fleet-reject-${v.id}`} onClick={() => review(v.id, false)} className="rounded-full border border-border px-4 py-2 text-xs font-semibold hover:text-destructive">Reject</button>
          </div>
        </div>
      ))}
    </div>
  );
}

function Restaurants() {
  const qc = useQueryClient();
  const [notes, setNotes] = useState({});
  const { data } = useQuery({ queryKey: ["insp-restaurants"], queryFn: async () => (await api.get("/eat/inspector/restaurants")).data.restaurants });
  const rows = (data || []).slice().sort((a, b) => (a.approval_status === "pending" ? -1 : 1));
  const viewDoc = async (id, type) => {
    try { const res = await api.get(`/eat/restaurant/${id}/document/${type}`, { responseType: "blob" }); window.open(URL.createObjectURL(res.data), "_blank"); }
    catch { toast.error("Could not open document"); }
  };
  const review = async (id, approve) => {
    try { await api.post(`/eat/inspector/restaurants/${id}/review`, { approve, notes: notes[id] || "" }); toast.success(approve ? "Restaurant approved" : "Restaurant rejected"); qc.invalidateQueries({ queryKey: ["insp-restaurants"] }); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="space-y-3" data-testid="insp-restaurants">
      {rows.length === 0 && <p className="rounded-2xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">No restaurant applications.</p>}
      {rows.map((r) => (
        <div key={r.id} data-testid={`insp-restaurant-${r.id}`} className="rounded-2xl border border-border bg-card p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="text-sm font-semibold">{r.name} <span className="text-xs text-muted-foreground">· {r.owner_name || r.owner_email}</span></p>
              <p className="text-xs text-muted-foreground">{[r.cuisine, r.business_reg_number && `Reg ${r.business_reg_number}`, r.phone, r.address].filter(Boolean).join(" · ")}</p>
            </div>
            <StatusPill status={r.approval_status} />
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {["business_license", "owner_id", "storefront_photo"].map((t) => (
              <button key={t} data-testid={`insp-rest-doc-${r.id}-${t}`} disabled={!r.documents?.includes(t)} onClick={() => viewDoc(r.id, t)}
                className="flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs font-semibold disabled:opacity-30"><Eye size={13} /> {t.replace(/_/g, " ")}</button>
            ))}
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <input data-testid={`insp-rest-notes-${r.id}`} value={notes[r.id] || ""} onChange={(e) => setNotes((x) => ({ ...x, [r.id]: e.target.value }))} placeholder="Notes" className="min-w-[160px] flex-1 rounded-xl border border-border bg-background px-3 py-2 text-xs outline-none focus:border-primary" />
            <button data-testid={`insp-rest-approve-${r.id}`} disabled={!["business_license","owner_id","storefront_photo"].every((t) => r.documents?.includes(t))} onClick={() => review(r.id, true)} className="rounded-full bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground disabled:opacity-40">Approve</button>
            <button data-testid={`insp-rest-reject-${r.id}`} onClick={() => review(r.id, false)} className="rounded-full border border-border px-4 py-2 text-xs font-semibold hover:text-destructive">Reject</button>
          </div>
        </div>
      ))}
    </div>
  );
}

function Businesses() {
  const qc = useQueryClient();
  const [notes, setNotes] = useState({});
  const { data } = useQuery({ queryKey: ["insp-businesses"], queryFn: async () => (await api.get("/fleet/inspector/businesses")).data.businesses });
  const rows = (data || []).slice().sort((a, b) => (a.approval_status === "pending" ? -1 : 1));
  const viewDoc = async (id, type) => {
    try { const res = await api.get(`/fleet/business/${id}/document/${type}`, { responseType: "blob" }); window.open(URL.createObjectURL(res.data), "_blank"); }
    catch { toast.error("Could not open document"); }
  };
  const review = async (id, approve) => {
    try { await api.post(`/fleet/inspector/businesses/${id}/review`, { approve, notes: notes[id] || "" }); toast.success(approve ? "Business approved" : "Business rejected"); qc.invalidateQueries({ queryKey: ["insp-businesses"] }); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="space-y-3" data-testid="insp-businesses">
      {rows.length === 0 && <p className="rounded-2xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">No business applications.</p>}
      {rows.map((b) => (
        <div key={b.id} data-testid={`insp-business-${b.id}`} className="rounded-2xl border border-border bg-card p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="text-sm font-semibold">{b.business_name} <span className="text-xs text-muted-foreground">· {b.owner_name || b.owner_email}</span></p>
              <p className="text-xs text-muted-foreground">{[b.reg_number && `Reg ${b.reg_number}`, b.owner_phone, b.contact_email, b.address].filter(Boolean).join(" · ")}</p>
            </div>
            <StatusPill status={b.approval_status} />
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {["business_license", "owner_id", "tax_certificate"].map((t) => (
              <button key={t} data-testid={`insp-biz-doc-${b.id}-${t}`} disabled={!b.documents?.includes(t)} onClick={() => viewDoc(b.id, t)}
                className="flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs font-semibold disabled:opacity-30"><Eye size={13} /> {t.replace(/_/g, " ")}</button>
            ))}
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <input data-testid={`insp-biz-notes-${b.id}`} value={notes[b.id] || ""} onChange={(e) => setNotes((x) => ({ ...x, [b.id]: e.target.value }))} placeholder="Notes" className="min-w-[160px] flex-1 rounded-xl border border-border bg-background px-3 py-2 text-xs outline-none focus:border-primary" />
            <button data-testid={`insp-biz-approve-${b.id}`} disabled={!["business_license","owner_id","tax_certificate"].every((t) => b.documents?.includes(t))} onClick={() => review(b.id, true)} className="rounded-full bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground disabled:opacity-40">Approve</button>
            <button data-testid={`insp-biz-reject-${b.id}`} onClick={() => review(b.id, false)} className="rounded-full border border-border px-4 py-2 text-xs font-semibold hover:text-destructive">Reject</button>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Inspector() {
  const [tab, setTab] = useState("drivers");
  const TABS = [["drivers", "Drivers", Car], ["restaurants", "Restaurants", Storefront], ["businesses", "Businesses", Buildings], ["fleets", "Fleet vehicles", Truck]];
  return (
    <div className="space-y-6" data-testid="inspector-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-display text-3xl font-semibold tracking-tight sm:text-4xl"><ShieldCheck size={30} weight="duotone" className="text-primary" /> Inspector Console</h1>
          <p className="mt-1 text-muted-foreground">Verify and approve drivers, restaurants, fleet businesses and their vehicles in your community.</p>
        </div>
        <div className="flex flex-wrap rounded-full border border-border bg-card p-1">
          {TABS.map(([v, l, Icon]) => (
            <button key={v} data-testid={`insp-tab-${v}`} onClick={() => setTab(v)} className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${tab === v ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}><Icon size={16} weight="bold" /> {l}</button>
          ))}
        </div>
      </div>
      {tab === "drivers" ? <Drivers /> : tab === "restaurants" ? <Restaurants /> : tab === "businesses" ? <Businesses /> : <Fleets />}
    </div>
  );
}
